import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Link2, Search, Network, Route } from 'lucide-react';
import { Container, Toolbar, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Input, Badge, Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Boxes } from 'lucide-react';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useVpcs, useDeleteVpc,
  usePeeringGroups, useCreatePeeringGroup, useDeletePeeringGroup, usePeeringSubnets,
  useCreatePeeringSubnet, useDeletePeeringSubnet,
  type Vpc as VpcT, type PeeringGroup, type PeeringSubnet,
} from '@/api/opennebula';
import { RowMenu, SimpleCreateDialog } from '../components/CrudBits';
import { CreateVpcDialog, EditVpcDialog, CopyIp } from './VpcDialogs';

type Tab = 'vpcs' | 'peering' | 'subnets';

/** VPC management home: VPCs, peering groups, and peering subnets. Rows open
 *  dedicated detail pages (subnet + member management), matching the legacy console. */
export function VpcList() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [tab, setTab] = useState<Tab>('vpcs');

  return (
    <Container size="xl">
      <PageHeader icon={<Boxes size={22} />} title="VPC" description="Virtual private clouds and peering between them." />
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="vpcs">VPCs</TabsTrigger>
          <TabsTrigger value="peering">Peering</TabsTrigger>
          <TabsTrigger value="subnets">Peering subnets</TabsTrigger>
        </TabsList>
        <TabsContent value="vpcs"><Vpcs serviceId={serviceId!} /></TabsContent>
        <TabsContent value="peering"><Peering serviceId={serviceId!} /></TabsContent>
        <TabsContent value="subnets"><PeeringSubnets serviceId={serviceId!} /></TabsContent>
      </Tabs>
    </Container>
  );
}

function Vpcs({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const base = `/services/compute/${serviceId}/vpc`;
  const { data: vpcs = [], isLoading } = useVpcs(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState<VpcT | null>(null);
  const [del, setDel] = useState<VpcT | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return vpcs;
    return vpcs.filter((v) => [v.name, v.id, v.cidr].some((f) => String(f ?? '').toLowerCase().includes(q)));
  }, [vpcs, query]);

  const remove = useDeleteVpc(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'VPC deleted' }); setDel(null); },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns: ColumnDef<VpcT, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'cidr', header: 'CIDR', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.cidr || '—'}</span> },
    { id: 'subnets', header: 'Subnets', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.subnet_count ?? row.original.subnets?.length ?? 0}</Badge> },
    { id: 'external_ip', header: 'External IP', cell: ({ row }) => row.original.router_public_ip ? <CopyIp ip={row.original.router_public_ip} /> : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span> },
    { id: 'router', header: 'Router', cell: ({ row }) => row.original.router_status ? <Badge tone="neutral" appearance="subtle" size="sm">{row.original.router_status}</Badge> : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'Manage subnets', onSelect: () => navigate(`${base}/${row.original.id}`) }, { label: 'Edit', onSelect: () => setEdit(row.original) }, { label: 'Delete', danger: true, onSelect: () => setDel(row.original) }]} /> },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search VPCs" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 320 }} />}
        end={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New VPC</Button>}
      />
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(v) => String(v.id)}
          onRowClick={(v) => navigate(`${base}/${v.id}`)}
          emptyState={vpcs.length === 0
            ? <EmptyState icon={<Network size={28} />} title="No VPCs yet" description="Create a virtual private cloud with its own CIDR." actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New VPC</Button>} />
            : 'No VPCs match your search.'}
        />
      )}
      <CreateVpcDialog open={createOpen} onOpenChange={setCreateOpen} serviceId={serviceId} />
      {edit && <EditVpcDialog serviceId={serviceId} vpc={edit} onClose={() => setEdit(null)} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete VPC?" description={`Remove “${del?.name || del?.id}” and its subnets?`} confirmLabel="Delete" destructive loading={remove.isPending} onConfirm={() => del && remove.mutate(del.id)} />
    </>
  );
}

function Peering({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const base = `/services/compute/${serviceId}/vpc`;
  const { data: groups = [], isLoading } = usePeeringGroups(serviceId);
  const { data: peeringSubnets = [] } = usePeeringSubnets(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState<PeeringGroup | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => [g.name, g.id].some((f) => String(f ?? '').toLowerCase().includes(q)));
  }, [groups, query]);

  const remove = useDeletePeeringGroup(serviceId, { onSuccess: () => { show({ tone: 'success', title: 'Peering group deleted' }); setDel(null); }, onError: () => show({ tone: 'danger', title: 'Delete failed' }) });

  const columns: ColumnDef<PeeringGroup, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'peering_subnet', header: 'Peering subnet', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.peering_subnet?.name || '—'}</span> },
    { id: 'members', header: 'Members', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.members?.length ?? 0}</Badge> },
    { id: 'status', header: 'Status', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.status || '—'}</Badge> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'Manage VPCs', onSelect: () => navigate(`${base}/peering/${row.original.id}`) }, { label: 'Delete', danger: true, onSelect: () => setDel(row.original) }]} /> },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search peering groups" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 320 }} />}
        end={<Button size="sm" startIcon={<Link2 size={15} />} onClick={() => setCreateOpen(true)}>New group</Button>}
      />
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(g) => String(g.id)}
          onRowClick={(g) => navigate(`${base}/peering/${g.id}`)}
          emptyState={groups.length === 0
            ? <EmptyState icon={<Link2 size={28} />} title="No peering groups yet" description="Peer multiple VPCs so they can route to each other." actions={<Button size="sm" startIcon={<Link2 size={15} />} onClick={() => setCreateOpen(true)}>New group</Button>} />
            : 'No peering groups match your search.'}
        />
      )}
      <SimpleCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New peering group"
        fields={[
          { key: 'name', label: 'Name', placeholder: 'prod-peering' },
          { key: 'peering_subnet_id', label: 'Peering subnet', type: 'select', placeholder: 'Select a transit subnet', options: peeringSubnets.map((s) => ({ value: s.id, label: `${s.name || s.id}${s.ip_range ? ` (${s.ip_range})` : ''}` })) },
        ]}
        onCreate={useCreatePeeringGroup(serviceId, { onSuccess: () => { show({ tone: 'success', title: 'Group created' }); setCreateOpen(false); }, onError: () => show({ tone: 'danger', title: 'Create failed' }) })}
      />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete peering group?" description={`Remove “${del?.name || del?.id}”?`} confirmLabel="Delete" destructive loading={remove.isPending} onConfirm={() => del && remove.mutate(del.id)} />
    </>
  );
}

function PeeringSubnets({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const { data: subnets = [], isLoading } = usePeeringSubnets(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState<PeeringSubnet | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subnets;
    return subnets.filter((s) => [s.name, s.id, s.ip_range].some((f) => String(f ?? '').toLowerCase().includes(q)));
  }, [subnets, query]);

  const remove = useDeletePeeringSubnet(serviceId, { onSuccess: () => { show({ tone: 'success', title: 'Peering subnet deleted' }); setDel(null); }, onError: () => show({ tone: 'danger', title: 'Delete failed' }) });

  const columns: ColumnDef<PeeringSubnet, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'ip_range', header: 'IP range', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.ip_range || '—'}</span> },
    { id: 'status', header: 'Status', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.status || '—'}</Badge> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'Delete', danger: true, onSelect: () => setDel(row.original) }]} /> },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search peering subnets" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 320 }} />}
        end={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New subnet</Button>}
      />
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(s) => String(s.id)}
          emptyState={subnets.length === 0
            ? <EmptyState icon={<Route size={28} />} title="No peering subnets yet" description="Reserve a subnet that peering groups can route through." actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New subnet</Button>} />
            : 'No peering subnets match your search.'}
        />
      )}
      <SimpleCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New peering subnet"
        fields={[{ key: 'name', label: 'Name', placeholder: 'peering-subnet' }, { key: 'ip_range', label: 'CIDR', placeholder: '10.0.0.0/24' }]}
        onCreate={useCreatePeeringSubnet(serviceId, { onSuccess: () => { show({ tone: 'success', title: 'Subnet created' }); setCreateOpen(false); }, onError: () => show({ tone: 'danger', title: 'Create failed' }) })}
      />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete peering subnet?" description={`Remove “${del?.name || del?.id}”?`} confirmLabel="Delete" destructive loading={remove.isPending} onConfirm={() => del && remove.mutate(del.id)} />
    </>
  );
}
